import apiClient from './client';
import type { ApiResponse } from '../types';

export interface VendorConversation {
  id: string;
  subject: string | null;
  status: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  created_at: string | null;
}

export interface VendorMessage {
  id: string;
  conversation_id: string;
  sender_type: 'admin' | 'vendor';
  sender_admin_user: string | null;
  body: string;
  created_at: string | null;
}

export interface ThreadResponse {
  conversation: VendorConversation;
  messages: VendorMessage[];
}

export interface SendMessageResponse {
  message: VendorMessage;
  conversation: VendorConversation;
}

/**
 * Wave-75 vendor portal messaging API. Mirrors the admin side at
 * /api/v1/vendor-messages — both write to the same Conversation + Message
 * tables but address differently (vendor by conversation_id, admin by
 * vendor_id since the admin always knows the vendor up front).
 */
export const messagesApi = {
  async listConversations(): Promise<VendorConversation[]> {
    const res = await apiClient.get<ApiResponse<{ conversations: VendorConversation[] }>>(
      '/vendor/messages',
    );
    return res.data.data.conversations || [];
  },

  async unreadCount(): Promise<number> {
    const res = await apiClient.get<ApiResponse<{ unread: number }>>(
      '/vendor/messages/unread-count',
    );
    return res.data.data.unread || 0;
  },

  async getThread(conversationId: string): Promise<ThreadResponse> {
    const res = await apiClient.get<ApiResponse<ThreadResponse>>(
      `/vendor/messages/${conversationId}`,
    );
    return res.data.data;
  },

  async send(body: string, subject?: string, conversationId?: string): Promise<SendMessageResponse> {
    const res = await apiClient.post<ApiResponse<SendMessageResponse>>(
      '/vendor/messages',
      { body, subject, conversation_id: conversationId },
    );
    return res.data.data;
  },

  async markRead(conversationId: string): Promise<void> {
    await apiClient.post(`/vendor/messages/${conversationId}/mark-read`);
  },
};
